/**
 * AmortisationKachel (D-084, Prompt 127 Aufgabe 1) — Portfolio-Kachel
 * „Amortisation kumuliert (Ist + Plan)" neben den kumulierten KPI-Karten.
 *
 * Quelle: Backend `amortisation_kumuliert` = Σ über pph["members"] der
 * Rückführungen Bank + Gesellschafterdarlehen aus fcfe.jahr (Curare/GE nie
 * enthalten, I10). Die Werte kommen NEGATIV (Rückführung ≤ 0 beim Erzeuger,
 * fcfe.py split_bewegung); die Kachel zeigt den BETRAG.
 *
 * Warum „Ist YTD*" mit Fußnote: fcfe.jahr.*.ist ist die Summe der AKTIVEN
 * Quartale. Im laufenden Quartal sind die Restmonate mit der aktuellen
 * Tilgungsrate hochgerechnet (fcfe.py, D-078) — kein reines Buchungs-Ist.
 * Die Fußnote sagt das, statt es zu verschweigen.
 *
 * HARTE null-Regel (SCHEMA_MAPPING §5): null → „—", NIE 0.
 */
import type { AmortisationKumuliert } from '../types/snapshot';
import { formatEur } from '../lib/format';
import { KpiCard } from './KpiCard';
import { JAHRE_FALLBACK, useJahre } from '../lib/jahre';
import type { Jahre } from '../lib/jahre';

/**
 * Betrag statt Vorzeichen. Math.abs statt Negation: -(0) ergäbe -0 und würde als
 * „-0 €" formatiert. Ein positiver Rückführungswert kommt beim Erzeuger nicht vor
 * (split_bewegung liefert min(net, 0)).
 */
function betrag(v: number | null | undefined): number | null {
  return v === null || v === undefined ? null : Math.abs(v);
}

/**
 * Beschriftung und Fußnote aus EINER Quelle (Muster NICHT_PORTFOLIO_TEXTE).
 * D-089: „Plan Rest" nennt das Berichtsjahr aus meta, nicht mehr fest 2026.
 */
export function amortisationTexte({ bj }: Jahre) {
  return {
    titel: 'Amortisation kumuliert (Ist + Plan)',
    ist: 'Ist YTD*',
    plan: `Plan Rest ${bj}`,
    fussnote:
      '* Amortisation = Rückführung Bankdarlehen + Gesellschafterdarlehen ' +
      '(FCFE-Bausteine), Summe der Portfolio-Gesellschaften, als Betrag. ' +
      'Ist = aktive Quartale; im laufenden Quartal sind die Restmonate mit der ' +
      'aktuellen Tilgungsrate hochgerechnet. Plan = künftige Quartale zur aktuellen ' +
      'Ist-Tilgungsrate; Gesellschafterdarlehen ereignisbasiert ohne Fortschreibung (D-078).',
  } as const;
}

/** Texte für die Fallback-Jahre (Snapshot vor D-089). */
export const AMORTISATION_TEXTE = amortisationTexte(JAHRE_FALLBACK);

export interface AmortisationProps {
  amortisation?: AmortisationKumuliert | null;
}

export function AmortisationKachel({ amortisation }: AmortisationProps) {
  const T = amortisationTexte(useJahre()); // Hook vor dem frühen return
  // Älterer Backend-Stand ohne den Key: keine Kachel statt einer „—"-Kachel.
  if (!amortisation) return null;
  return (
    <KpiCard
      label={T.titel}
      value={betrag(amortisation.gesamt)}
      footer={
        <>
          {T.ist} {formatEur(betrag(amortisation.ist))} ·{' '}
          {T.plan} {formatEur(betrag(amortisation.plan))}
        </>
      }
    />
  );
}

export function AmortisationFussnote({ amortisation }: AmortisationProps) {
  if (!amortisation) return null;
  return (
    <p className="mt-2 text-xs text-text-muted">
      {AMORTISATION_TEXTE.fussnote}
      {amortisation.stand ? ` Stand: ${amortisation.stand}.` : ''}
    </p>
  );
}
